'use client';

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-non-null-assertion */

import { useEffect, useRef, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { useRequest } from 'ahooks';
import { debounce } from 'lodash-es';
import {
  ChainType,
  getHackerAddressTopList,
  HackerAddressTopListResp,
} from '@/services/home';
import { ReactFlowProvider, Controls, ControlButton } from '@xyflow/react';
import { Spin } from '@bfe/moly';
import { formatNumber } from '@web3/base-utils';
import { useFm } from '@web3/base-hooks';
import { EnterFullScreenIcon, ExitFullScreenIcon } from '@radix-ui/react-icons';
import { CommonLoading } from '@/components/CommonLoading';

interface Link {
  source: string | Node;
  target: string | Node;
  value?: number;
  color?: string;
  style?: {
    stroke: string;
    strokeWidth: number;
  };
  data?: {
    selectColor: string;
  };
}

const chainColors = {
  [ChainType.BTC]: '#F7931A',
  [ChainType.ETH]: '#627EEA',
  [ChainType.BNB]: '#F0B90B',
  [ChainType.ARB]: '#FFF',
  [ChainType.SOL]: '#8F72E1',
  [ChainType.TRON]: '#FF0013',
};

const chainNames = {
  [ChainType.BTC]: 'Bitcoin',
  [ChainType.ETH]: 'Ethereum',
  [ChainType.BNB]: 'BNB',
  [ChainType.ARB]: 'Arbitrum',
  [ChainType.SOL]: 'Solana',
  [ChainType.TRON]: 'Tron',
};

// 创建统一的 tooltip 样式
const createTooltipStyle = (
  tooltipContainer: d3.Selection<SVGGElement, unknown, null, undefined>,
  level: number,
  isOthers: boolean
) => {
  // 根据层级设置高宽
  const height = isOthers ? 60 : level === 2 ? 100 : 80;
  const width = level === 2 ? 150 : 220;

  // hover 框背景
  tooltipContainer
    .append('rect')
    .attr('width', width)
    .attr('height', height)
    .attr('rx', 8) // border-radius
    .attr('ry', 8) // border-radius
    .attr('fill', '#404347') // background
    .attr('stroke', '#4B5563')
    .attr('stroke-width', 1)
    .style('pointer-events', 'all')
    .style('cursor', 'default');
  // 使用 transform 来模拟 padding
  const content = tooltipContainer
    .append('g')
    .attr('transform', 'translate(12, 12)'); // 添加 12px padding

  return content;
};

// Tooltip 文本的公共样式
const createText = (
  container: d3.Selection<SVGGElement, unknown, null, undefined>,
  options: {
    text: string;
    x?: number;
    fill?: string;
    fontWeight?: string;
    textDecoration?: string;
  }
) => {
  return container
    .append('text')
    .attr('font-size', '12px')
    .attr('fill', options.fill || '#94A3B8')
    .attr('x', options.x || 0)
    .attr('font-weight', options.fontWeight || 'normal')
    .attr('text-decoration', options.textDecoration || 'none')
    .text(options.text);
};

interface Node {
  id: string;
  label: string;
  level: number;
  size: number;
  group?: string;
  isSquare?: boolean;
  fx?: number;
  fy?: number;
  x?: number;
  y?: number;
  value?: string;
  icon?: {
    path: string;
    color: string;
    viewBox: string;
  };
  isOthers?: boolean;
}

interface Link {
  source: string | Node;
  target: string | Node;
  value?: number;
  color?: string;
  style?: {
    stroke: string;
    strokeWidth: number;
  };
  data?: {
    selectColor: string;
  };
}

const lineWidth = 0.8;

const Bubble = () => {
  const fm = useFm();
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [hackerAddressTopList, setHackerAddressTopList] =
    useState<HackerAddressTopListResp>({
      chainAssetList: [],
      totalAmount: '0',
    });

  const { loading } = useRequest(() => getHackerAddressTopList(), {
    onSuccess: (res) => {
      const { code, success, result } = res || {};
      if (code === 0 && success) {
        setHackerAddressTopList(result);
      }
    },
  });

  // 添加一个获取尺寸的函数
  const updateDimensions = useCallback(() => {
    if (containerRef.current) {
      const containerHeight = containerRef.current.clientHeight || 720;
      // 只更新尺寸状态，不触发缩放
      setDimensions({
        width: containerRef.current.clientWidth,
        height: containerHeight,
      });
    }
  }, []);

  // 监听窗口大小变化
  useEffect(() => {
    updateDimensions();
    const handleResize = debounce(updateDimensions, 200);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updateDimensions]);

  useEffect(() => {
    if (
      !hackerAddressTopList ||
      !Array.isArray(hackerAddressTopList.chainAssetList) ||
      hackerAddressTopList.chainAssetList.length === 0
    )
      return;

    if (!svgRef.current || !dimensions.width || !dimensions.height) return;

    // 清除之前的内容，避免内存泄漏
    const cleanup = () => {
      if (svgRef.current) {
        d3.select(svgRef.current).selectAll('*').remove();
      }
    };

    const width = dimensions.width;
    const height = dimensions.height;
    const centerX = width / 2;
    const centerY = height / 2;

    // 找出所有第三级节点的 holdingValue 范围
    const holdingValues = hackerAddressTopList.chainAssetList.flatMap((chain) =>
      chain.topAddressList.map((address) => Number(address.holdingValue))
    );
    const minHoldingValue = Math.min(...holdingValues);
    const maxHoldingValue = Math.max(...holdingValues);

    // 定义半径比例尺
    const radiusScale = d3
      .scaleSqrt()
      .domain([minHoldingValue, maxHoldingValue])
      .range([5, 30]); // 直接指定半径范围：5-30 像素

    // 添加一个函数，用于限制第三级节点的最大半径为300px
    const limitNodeSize = (size: number) => {
      return Math.min(size, 45); // 限制最大半径为300px
    };

    const secondLevelScale = d3
      .scaleSqrt()
      .domain([
        0,
        Math.max(
          ...hackerAddressTopList.chainAssetList.map((chain) =>
            Number(chain.totalAmount)
          )
        ),
      ])
      .range([27.5, 27.5]);

    // Create nodes
    const nodes: Node[] = [
      // Center node
      {
        id: 'center',
        label: 'Lazarus Wallets',
        level: 1,
        size: 60,
        isSquare: true,
        x: centerX, // 设置初始 x 位置
        y: centerY, // 设置初始 y 位置
        fx: centerX, // 固定 x 位置
        fy: centerY, // 固定 y 位置
      },

      // 第二级节点，链
      ...hackerAddressTopList.chainAssetList.map((chain) => ({
        id: chain.chainName,
        label: chain.chainName,
        level: 2,
        size: secondLevelScale(Number(chain.totalAmount)),
        value: chain.totalAmount,
        x: centerX, // 设置初始 x 位置为中心
        y: centerY, // 设置初始 y 位置为中心
      })),

      // 第三级节点，地址
      ...hackerAddressTopList.chainAssetList.flatMap((chain) =>
        chain.topAddressList.map((address, index) => ({
          id: `${chain.chainName}-${index}`,
          label: address.address,
          level: 3,
          size: radiusScale(Number(address.holdingValue)),
          group: chain.chainName,
          value: address.holdingValue,
        }))
      ),

      // 添加表示 othersTotalAmount 的节点
      ...hackerAddressTopList.chainAssetList
        .filter((chain) => Number(chain.othersTotalAmount) > 0) // 只添加有其他金额的链
        .map((chain) => ({
          id: `${chain.chainName}-others`,
          label: 'Others',
          level: 3,
          size: limitNodeSize(radiusScale(Number(chain.othersTotalAmount))),
          group: chain.chainName,
          value: chain.othersTotalAmount,
          isOthers: true, // 标记为"其他"节点
        })),
    ];

    // 在创建连接线之前，添加一个辅助函数来计算线段位置
    const calculateLinePosition = (source: any, target: any) => {
      const dx = target.x - source.x;
      const dy = target.y - source.y;
      const angle = Math.atan2(dy, dx);

      // 根据源节点和目标节点的大小和形状调整线段位置
      let sourceSize, targetSize;

      // 根据节点级别设置合适的偏移量
      if (source.level === 1) {
        // 中心节点是圆形
        sourceSize = (source.size || 45) * 0.8;
      } else if (source.level === 2) {
        // 二级节点是圆形
        sourceSize = (source.size || 27.5) * 0.8;
      } else {
        // 第三级节点是矩形
        const size = source.size || 10;
        // 根据角度计算矩形边缘到中心的距离
        // 使用矩形的半宽和半高
        const halfWidth = size;
        const halfHeight = size;

        // 计算从中心到矩形边缘的距离（根据角度）
        if (Math.abs(Math.cos(angle)) > 0.0001) {
          // 主要是水平方向
          const tx = halfWidth / Math.abs(Math.cos(angle));
          const ty = halfHeight / Math.abs(Math.sin(angle) || 0.0001);
          sourceSize = Math.min(tx, ty);
        } else {
          // 主要是垂直方向
          sourceSize = halfHeight;
        }
      }

      if (target.level === 1) {
        // 中心节点是圆形
        targetSize = (target.size || 45) * 0.8;
      } else if (target.level === 2) {
        // 二级节点是圆形
        targetSize = (target.size || 27.5) * 0.8;
      } else {
        // 第三级节点是矩形
        const size = target.size || 10;
        // 根据角度计算矩形边缘到中心的距离
        // 使用矩形的半宽和半高
        const halfWidth = size;
        const halfHeight = size;

        // 计算从中心到矩形边缘的距离（根据角度）
        if (Math.abs(Math.cos(angle)) > 0.0001) {
          // 主要是水平方向
          const tx = halfWidth / Math.abs(Math.cos(angle));
          const ty = halfHeight / Math.abs(Math.sin(angle) || 0.0001);
          targetSize = Math.min(tx, ty);
        } else {
          // 主要是垂直方向
          targetSize = halfHeight;
        }
      }

      // 计算线段起点应该在的位置（源节点的边缘）
      const startX = source.x + Math.cos(angle) * sourceSize;
      const startY = source.y + Math.sin(angle) * sourceSize;

      // 计算线段末端应该在的位置（目标节点的边缘）
      const endX = target.x - Math.cos(angle) * targetSize;
      const endY = target.y - Math.sin(angle) * targetSize;

      // 返回线段的起点（源节点边缘）和终点（目标节点边缘）
      return {
        x1: startX,
        y1: startY,
        x2: endX,
        y2: endY,
      };
    };

    // Create links
    const links: Link[] = [
      // Connect center to second level
      ...hackerAddressTopList.chainAssetList.map((chain) => ({
        source: 'center',
        target: chain.chainName,
        value: Number(chain.totalAmount),
        color: chainColors[chain.chainName],
        style: {
          stroke: chainColors[chain.chainName],
          strokeWidth: lineWidth,
        },
        data: {
          selectColor: chainColors[chain.chainName],
        },
      })),

      // Connect second level to their respective third level nodes
      ...nodes
        .filter((n) => n.level === 3)
        .map((n) => ({
          source: n.group || '',
          target: n.id,
          value: Number(n.value),
          color: n.group ? chainColors[n.group as ChainType] : '#CCCCCC',
          style: {
            stroke: n.group ? chainColors[n.group as ChainType] : '#CCCCCC',
            strokeWidth: lineWidth,
          },
          animated: true,
          data: {
            selectColor: n.group
              ? chainColors[n.group as ChainType]
              : '#CCCCCC',
          },
        })),
    ];

    const svg = d3
      .select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', [0, 0, width, height]);

    // 创建一个defs元素用于定义渐变和遮罩
    const defs = svg.append('defs');

    // 为每个第三级节点创建一个径向渐变
    nodes.forEach((node) => {
      if (node.level === 3) {
        const nodeColor = node.group
          ? chainColors[node.group as ChainType]
          : '#CCCCCC';
        const gradientId = `node-gradient-${node.id}`;

        const gradient = defs
          .append('radialGradient')
          .attr('id', gradientId)
          .attr('cx', '50%')
          .attr('cy', '50%')
          .attr('r', '50%')
          .attr('fx', '50%')
          .attr('fy', '50%');

        if (node.isOthers) {
          // Others 节点的渐变样式
          // 渐变中心是半透明的
          gradient
            .append('stop')
            .attr('offset', '0%')
            .attr('stop-color', nodeColor)
            .attr('stop-opacity', 0.1);

          // 渐变中间区域
          gradient
            .append('stop')
            .attr('offset', '80%')
            .attr('stop-color', nodeColor)
            .attr('stop-opacity', 0.15);

          // 渐变边缘区域
          gradient
            .append('stop')
            .attr('offset', '92%')
            .attr('stop-color', nodeColor)
            .attr('stop-opacity', 0.3);

          // 渐变最外层
          gradient
            .append('stop')
            .attr('offset', '100%')
            .attr('stop-color', nodeColor)
            .attr('stop-opacity', 0.5);
        } else {
          // 普通节点的渐变样式
          // 渐变中心是半透明的
          gradient
            .append('stop')
            .attr('offset', '0%')
            .attr('stop-color', nodeColor)
            .attr('stop-opacity', 0.15);

          // 渐变中间区域
          gradient
            .append('stop')
            .attr('offset', '80%')
            .attr('stop-color', nodeColor)
            .attr('stop-opacity', 0.2);

          // 渐变边缘区域
          gradient
            .append('stop')
            .attr('offset', '92%')
            .attr('stop-color', nodeColor)
            .attr('stop-opacity', 0.4);

          // 渐变最外层
          gradient
            .append('stop')
            .attr('offset', '100%')
            .attr('stop-color', nodeColor)
            .attr('stop-opacity', 0.6);
        }
      }
    });

    // 创建一个 g 元素作为所有其他元素的容器
    const g = svg.append('g');

    // 创建缩放行为
    const zoom = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      })
      .on('start', () => {
        try {
          svg.classed('grabbing', true).classed('grab', false);
        } catch (e) {
          console.warn('Failed to set cursor class on zoom start', e);
        }
      })
      .on('end', () => {
        try {
          svg.classed('grabbing', false).classed('grab', true);
        } catch (e) {
          console.warn('Failed to set cursor class on zoom end', e);
        }
      });

    // 将缩放行为应用到 SVG 元素
    svg.call(zoom);

    // force simulation
    // 力导向调整
    const simulation = d3
      .forceSimulation(nodes as any)
      .alphaDecay(0.1) // 增加alpha衰减速度，加快收敛
      .velocityDecay(0.5) // 增加速度衰减，减少震荡
      .alpha(0.3) // 增加初始alpha值，使布局更有活力
      .force(
        'link',
        d3
          .forceLink(links)
          .id((d: any) => d.id)
          .distance((d: any) => {
            // 为BTC和ETH的连接线增加距离
            if (d.source.level === 1 && d.target.level === 2) {
              if (
                d.target.id === ChainType.BTC ||
                d.target.id === ChainType.ETH
              ) {
                return 360 * 1.2; // 增加20%的距离
              }
            }
            if (d.source.level === 1 || d.target.level === 1) return 360;
            if (d.source.level === 2 || d.target.level === 2) return 100;
            return 50;
          })
      )
      .force(
        'charge',
        d3.forceManyBody().strength((d: any) => {
          if (d.level === 3) return -200; // 减小三级节点的排斥力
          if (d.level === 2) return -400; // 增加二级节点的排斥力
          return -200;
        })
      )
      .force('center', d3.forceCenter(centerX, centerY).strength(1)) // 增加中心力的强度
      .force(
        'collision',
        d3.forceCollide().radius((d: any) => {
          // 增加二级节点的碰撞半径，防止三级节点太靠近
          if (d.level === 2) return d.size + 60;
          return d.size + 20;
        })
      )
      .force(
        'radial',
        d3
          .forceRadial(
            (d: any) => {
              // 为不同层级设置不同的径向距离
              if (d.level === 2) {
                // 为BTC和ETH增加径向距离
                if (d.id === ChainType.BTC || d.id === ChainType.ETH) {
                  return 320 * 1.2; // 增加20%的径向距离
                }
                return 320; // 二级节点的径向距离
              }
              if (d.level === 3) return 260; // 三级节点的径向距离
              return 0; // 中心节点
            },
            centerX,
            centerY
          )
          .strength((d: any) => {
            // 增加径向力的强度
            if (d.level === 2) return 1; // 二级节点完全固定在径向位置
            if (d.level === 3) return 0.9; // 三级节点较强的径向力
            return 0; // 中心节点不受径向力影响
          })
      );

    // 设置第二级节点的位置
    nodes.forEach((node) => {
      if (node.level === 2) {
        const chainList = hackerAddressTopList.chainAssetList;
        const chainIndex = chainList.findIndex(
          (chain) => chain.chainName === node.id
        );
        if (chainIndex !== -1) {
          // 根据链的类型设置固定位置
          let angle;
          // 默认距离
          let distance = 320;

          // 根据链类型设置固定位置
          switch (node.id as ChainType) {
            case ChainType.ARB:
              // ARB 在最上方
              angle = -Math.PI / 2; // -90度，正上方
              break;
            case ChainType.BNB:
              // BNB 在最下方
              angle = Math.PI / 2; // 90度，正下方
              break;
            case ChainType.BTC:
              // BTC 在左侧
              angle = Math.PI; // 180度，正左方
              // 增加20%的距离
              distance = 384; // 320 * 1.2 = 384
              break;
            case ChainType.ETH:
              // ETH 在右侧
              angle = 0; // 0度，正右方
              // 增加20%的距离
              distance = 384; // 320 * 1.2 = 384
              break;
            case ChainType.TRON:
              // TRON 在左上方
              angle = (-3 * Math.PI) / 4; // -135度，左上方
              break;
            case ChainType.SOL:
              // SOL 在右上方
              angle = -Math.PI / 4; // -45度，右上方
              break;
            default: {
              // 其他链均匀分布在圆周上
              // 计算当前链在排序后的位置
              const sortedChains = [...chainList].sort(
                (a, b) => Number(b.totalAmount) - Number(a.totalAmount)
              );
              const sortedIndex = sortedChains.findIndex(
                (chain) => chain.chainName === node.id
              );
              // 均匀分布在圆周上，避开已分配的位置
              angle =
                (2 * Math.PI * sortedIndex) / Math.max(sortedChains.length, 1);
            }
          }

          // 设置节点位置
          node.fx = centerX + Math.cos(angle) * distance;
          node.fy = centerY + Math.sin(angle) * distance;
        }
      }
    });

    // 添加自定义力，使第三级节点围绕其对应的第二级节点
    const customForce = () => {
      return (alpha: number) => {
        // 对每个第三级节点
        nodes.forEach((node: any) => {
          if (node.level === 3 && node.group) {
            // 找到对应的第二级节点
            const parentNode = nodes.find((n) => n.id === node.group);
            if (parentNode && parentNode.x && parentNode.y) {
              // 计算从父节点到当前节点的角度
              const dx = node.x - parentNode.x;
              const dy = node.y - parentNode.y;
              const angle = Math.atan2(dy, dx);

              // 计算理想距离 - 从父节点向外的一定距离
              const idealDistance = 120; // 可以调整这个值

              // 计算理想位置 - 在父节点的理想距离处
              const idealX = parentNode.x + Math.cos(angle) * idealDistance;
              const idealY = parentNode.y + Math.sin(angle) * idealDistance;

              // 向理想位置移动，强度随alpha衰减
              node.vx += (idealX - node.x) * alpha * 0.3;
              node.vy += (idealY - node.y) * alpha * 0.3;
            }
          }
        });
      };
    };

    // 将自定义力添加到模拟中
    simulation.force('custom', customForce());

    // 初始化第三级节点的位置，使它们围绕各自的第二级节点
    nodes.forEach((node: any) => {
      if (node.level === 3 && node.group) {
        // 找到对应的第二级节点
        const parentNode = nodes.find((n) => n.id === node.group);
        if (
          parentNode &&
          parentNode.fx !== undefined &&
          parentNode.fy !== undefined
        ) {
          // 为每个第三级节点分配一个随机角度，但在父节点的一定扇区内
          const groupNodes = nodes.filter(
            (n) => n.level === 3 && n.group === node.group
          );
          const nodeIndex = groupNodes.indexOf(node);
          const totalNodes = groupNodes.length;

          // 计算该节点在组内的角度 - 在父节点周围的120度扇区内均匀分布
          const sectorAngle = (2 * Math.PI) / 3; // 120度扇区
          const baseAngle = Math.atan2(
            parentNode.fy - centerY,
            parentNode.fx - centerX
          );
          const nodeAngle =
            baseAngle -
            sectorAngle / 2 +
            (sectorAngle * nodeIndex) / Math.max(totalNodes - 1, 1);

          // 设置初始位置 - 在父节点外围一定距离
          const distance = 80 + Math.random() * 40; // 80-120之间的随机距离
          node.x = parentNode.fx + Math.cos(nodeAngle) * distance;
          node.y = parentNode.fy + Math.sin(nodeAngle) * distance;
        }
      }
    });

    // 调整渲染顺序：先创建连接线组
    const linkGroup = g.append('g').attr('class', 'links');

    // 创建所有连接线
    const link = linkGroup
      .selectAll('line')
      .data(links)
      .join('line')
      .attr('stroke', (d) => d.color || '#4B5563')
      .attr('stroke-width', lineWidth)
      .attr('stroke-opacity', 0.5)
      .attr('stroke-linecap', 'round')
      .attr('class', (d) => {
        // 为连接线添加类名，以便后续选择
        if (d.source === 'center' || (d.source as any).id === 'center') {
          return `link-center-to-${d.target instanceof Object ? d.target.id : d.target}`;
        } else if (d.source && d.target) {
          const sourceId = d.source instanceof Object ? d.source.id : d.source;
          const targetId = d.target instanceof Object ? d.target.id : d.target;
          return `link-${sourceId}-to-${targetId}`;
        }
        return '';
      });

    // 为连接添加标签，位置在线段上方3px处
    const linkLabels = linkGroup
      .selectAll('text')
      .data(
        links.filter((d) => {
          // 只保留从中心节点（source为'center'）到第二层的连接的文字
          return (d.source as any).id === 'center' || d.source === 'center';
        })
      )
      .join('text')
      .attr('fill', (d) => d.color || 'white')
      .attr('font-size', '12px')
      .attr('font-weight', 'bold')
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'auto')
      .text((d) => `$${formatNumber(d.value)}`);

    // 然后创建节点组，确保节点在连接线之上
    const nodeGroup = g.append('g').attr('class', 'nodes');

    // 按照层级顺序排序节点数据，确保第三级节点在最上层
    const sortedNodes = [...nodes].sort((a, b) => {
      // 层级高的节点（数字小的）先渲染，层级低的节点（数字大的）后渲染
      return a.level - b.level;
    });

    // Create the nodes
    const node = nodeGroup
      .selectAll('g')
      .data(sortedNodes)
      .join('g')
      .classed('grab', true);

    // Add shapes (squares or circles)
    node.each(function (d) {
      if (d.level === 2) {
        const g = d3.select(this);

        // 添加鼠标悬停事件处理
        g.style('cursor', 'pointer')
          .on('mouseenter', () => {
            // 降低所有连接线的不透明度
            link.attr('stroke-opacity', 0.1);

            // 高亮显示从中心到当前链节点的连接线
            linkGroup
              .selectAll(`.link-center-to-${d.id}`)
              .attr('stroke-opacity', 1)
              .attr('stroke-width', lineWidth * 2.5)
              .attr(
                'stroke',
                (d: any) => d.data?.selectColor || d.color || '#FFFFFF'
              );

            // 高亮显示从当前链节点到其所有子节点的连接线
            linkGroup
              .selectAll(`[class^="link-${d.id}-to-"]`)
              .attr('stroke-opacity', 0.7)
              .attr('stroke-width', lineWidth * 1.5);

            // 高亮显示当前链节点的标签
            linkLabels
              .filter(
                (l: any) =>
                  (l.target instanceof Object ? l.target.id : l.target) === d.id
              )
              .attr('font-size', '14px')
              .attr('font-weight', 'bolder');

            // 淡化所有节点
            node.style('opacity', 0.3);

            // 高亮显示中心节点、当前链节点及其子节点
            node
              .filter(
                (n: any) =>
                  n.id === 'center' || n.id === d.id || n.group === d.id
              )
              .style('opacity', 1);

            // 为高亮的节点添加发光效果
            node
              .filter((n: any) => n.id === d.id)
              .select('circle')
              .style(
                'filter',
                'drop-shadow(0px 0px 8px rgba(255, 255, 255, 0.8))'
              );
          })
          .on('mouseleave', () => {
            // 恢复所有连接线的不透明度和宽度
            link
              .attr('stroke-opacity', 0.5)
              .attr('stroke-width', lineWidth)
              .attr('stroke', (d) => d.color || '#4B5563');

            // 恢复标签大小和粗细
            linkLabels.attr('font-size', '12px').attr('font-weight', 'bold');

            // 恢复所有节点的不透明度
            node.style('opacity', 1);

            // 移除发光效果
            node.select('circle').style('filter', (n: any) => {
              if (n.level === 3) {
                return 'drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.3))';
              }
              return null;
            });
          });

        // 添加链的 SVG 图标
        switch (d.id as ChainType) {
          case ChainType.BTC: {
            const g = d3.select(this);

            // 创建一个新的 g 元素用于 BTC 图标
            const btcGroup = g
              .append('g')
              .attr('transform', `translate(${-32}, ${-32}) scale(1)`);

            // 使用原始 SVG 的 viewBox 和路径
            btcGroup
              .append('path')
              .attr(
                'd',
                'M63.04 39.741c-4.274 17.143-21.638 27.575-38.783 23.301C7.12 58.768-3.313 41.404.962 24.262 5.234 7.117 22.597-3.317 39.737.957c17.144 4.274 27.576 21.64 23.302 38.784z'
              )
              .attr('fill', '#f7931a');

            btcGroup
              .append('path')
              .attr(
                'd',
                'M46.11 27.441c.636-4.258-2.606-6.547-7.039-8.074l1.438-5.768-3.512-.875-1.4 5.616c-.922-.23-1.87-.447-2.812-.662l1.41-5.653-3.509-.875-1.439 5.766c-.764-.174-1.514-.346-2.242-.527l.004-.018-4.842-1.209-.934 3.75s2.605.597 2.55.634c1.422.355 1.68 1.296 1.636 2.042l-1.638 6.571c.098.025.225.061.365.117l-.37-.092-2.297 9.205c-.174.432-.615 1.08-1.609.834.035.051-2.552-.637-2.552-.637l-1.743 4.02 4.57 1.139c.85.213 1.683.436 2.502.646l-1.453 5.835 3.507.875 1.44-5.772c.957.26 1.887.5 2.797.726L27.504 50.8l3.511.875 1.453-5.823c5.987 1.133 10.49.676 12.383-4.738 1.527-4.36-.075-6.875-3.225-8.516 2.294-.531 4.022-2.04 4.483-5.157zM38.087 38.69c-1.086 4.36-8.426 2.004-10.807 1.412l1.928-7.729c2.38.594 10.011 1.77 8.88 6.317zm1.085-11.312c-.99 3.966-7.1 1.951-9.083 1.457l1.748-7.01c1.983.494 8.367 1.416 7.335 5.553z'
              )
              .attr('fill', '#ffffff');
            break;
          }
          case ChainType.ETH: {
            g.append('circle').attr('r', 27.5).attr('fill', '#627EEA');

            const ethScale = d.size / 16;
            const ethTranslateX = -16 * ethScale;
            const ethTranslateY = -16 * ethScale;

            g.append('path')
              .attr('d', 'M16.498 4v8.87l7.497 3.35z')
              .attr('fill', '#FFF')
              .attr('fill-opacity', '0.602')
              .attr(
                'transform',
                `translate(${ethTranslateX},${ethTranslateY}) scale(${ethScale})`
              );

            g.append('path')
              .attr('d', 'M16.498 4L9 16.22l7.498-3.35z')
              .attr('fill', '#FFF')
              .attr(
                'transform',
                `translate(${ethTranslateX},${ethTranslateY}) scale(${ethScale})`
              );

            g.append('path')
              .attr('d', 'M16.498 21.968v6.027L24 17.616z')
              .attr('fill', '#FFF')
              .attr('fill-opacity', '0.602')
              .attr(
                'transform',
                `translate(${ethTranslateX},${ethTranslateY}) scale(${ethScale})`
              );

            g.append('path')
              .attr('d', 'M16.498 27.995v-6.028L9 17.616z')
              .attr('fill', '#FFF')
              .attr(
                'transform',
                `translate(${ethTranslateX},${ethTranslateY}) scale(${ethScale})`
              );

            g.append('path')
              .attr('d', 'M16.498 20.573l7.497-4.353-7.497-3.348z')
              .attr('fill', '#FFF')
              .attr('fill-opacity', '0.2')
              .attr(
                'transform',
                `translate(${ethTranslateX},${ethTranslateY}) scale(${ethScale})`
              );

            g.append('path')
              .attr('d', 'M9 16.22l7.498 4.353v-7.701z')
              .attr('fill', '#FFF')
              .attr('fill-opacity', '0.602')
              .attr(
                'transform',
                `translate(${ethTranslateX},${ethTranslateY}) scale(${ethScale})`
              );
            break;
          }
          case ChainType.TRON: {
            // Add background circle
            g.append('circle').attr('r', 27.5).attr('fill', '#FF0013');

            const tronScale = d.size / 32;
            const tronTranslateX = -16 * tronScale;
            const tronTranslateY = -16 * tronScale;

            // Add TRON logo paths
            g.append('path')
              .attr(
                'd',
                'M1247.4,1018L711.8,573.8l875.3,161.1L1247.4,1018L1247.4,1018z M1209.2,1096.6l-87.3,722L651.1,633.6L1209.2,1096.6L1209.2,1096.6z M1290.1,1134.9l562.7-101.9l-645.4,786.3L1290.1,1134.9L1290.1,1134.9z M1680.1,767.8l185.8,176.6l-508.1,92L1680.1,767.8z'
              )
              .attr('fill', '#FFFFFF')
              .attr(
                'transform',
                `translate(${tronTranslateX},${tronTranslateY}) scale(${tronScale / 80})`
              );
            break;
          }
          case ChainType.SOL: {
            // Add background circle
            g.append('circle').attr('r', 27.5).attr('fill', '#000000');

            const solScale = d.size / 27.5; // 调整缩放比例
            const solTranslateX = -27.5; // 调整平移值
            const solTranslateY = -27.5; // 调整平移值

            // Create gradient definitions
            const solGradient = g
              .append('defs')
              .append('linearGradient')
              .attr('id', `sol-gradient-${d.id}`)
              .attr('gradientUnits', 'userSpaceOnUse')
              .attr('x1', '39.3146')
              .attr('y1', '13.2362')
              .attr('x2', '22.9962')
              .attr('y2', '44.124');

            solGradient
              .append('stop')
              .attr('offset', '0')
              .attr('stop-color', '#00FFA3');

            solGradient
              .append('stop')
              .attr('offset', '1')
              .attr('stop-color', '#DC1FFF');

            // Add Solana logo paths with adjusted viewBox
            const paths = [
              'M17.7101 33.5543C17.8851 33.3772 18.1257 33.2739 18.381 33.2739H41.5255C41.9485 33.2739 42.1599 33.7904 41.861 34.093L37.2889 38.7195C37.1139 38.8966 36.8733 38.9999 36.6181 38.9999H13.4735C13.0506 38.9999 12.8391 38.4834 13.1381 38.1809L17.7101 33.5543Z',
              'M17.7101 16.2804C17.8924 16.1033 18.133 16 18.381 16H41.5255C41.9485 16 42.1599 16.5165 41.861 16.8191L37.2889 21.4456C37.1139 21.6227 36.8733 21.726 36.6181 21.726H13.4735C13.0506 21.726 12.8391 21.2095 13.1381 20.907L17.7101 16.2804Z',
              'M37.2889 24.8619C37.1139 24.6848 36.8733 24.5815 36.6181 24.5815H13.4735C13.0506 24.5815 12.8391 25.0981 13.1381 25.4006L17.7101 30.0272C17.8851 30.2043 18.1257 30.3076 18.381 30.3076H41.5255C41.9485 30.3076 42.1599 29.791 41.861 29.4885L37.2889 24.8619Z',
            ];

            // 创建一个包含组以便统一变换
            const solGroup = g
              .append('g')
              .attr(
                'transform',
                `translate(${solTranslateX},${solTranslateY}) scale(${solScale})`
              );

            paths.forEach((path) => {
              solGroup
                .append('path')
                .attr('d', path)
                .attr('fill', `url(#sol-gradient-${d.id})`);
            });
            break;
          }
          case ChainType.BNB: {
            const g = d3.select(this);

            // 添加背景圆形
            g.append('circle').attr('r', 27.5).attr('fill', '#F0B90B');

            // 创建一个新的 g 元素用于 BNB 图标
            const bnbGroup = g
              .append('g')
              .attr(
                'transform',
                `translate(${-27.5}, ${-27.5}) scale(${55 / 2496})`
              );

            // 添加 BNB logo 的白色部分
            bnbGroup
              .append('path')
              .attr(
                'd',
                'M685.9,1248l0.9,330l280.4,165v193.2l-444.5-260.7v-524L685.9,1248L685.9,1248z M685.9,918v192.3l-163.3-96.6V821.4l163.3-96.6l164.1,96.6L685.9,918L685.9,918z M1084.3,821.4l163.3-96.6l164.1,96.6L1247.6,918L1084.3,821.4L1084.3,821.4z'
              )
              .attr('fill', '#FFFFFF');

            bnbGroup
              .append('path')
              .attr(
                'd',
                'M803.9,1509.6v-193.2l163.3,96.6v192.3L803.9,1509.6L803.9,1509.6z M1084.3,1812.2l163.3,96.6l164.1-96.6v192.3l-164.1,96.6l-163.3-96.6V1812.2L1084.3,1812.2z M1645.9,821.4l163.3-96.6l164.1,96.6v192.3l-164.1,96.6V918L1645.9,821.4L1645.9,821.4L1645.9,821.4z M1809.2,1578l0.9-330l163.3-96.6v524l-444.5,260.7v-193.2L1809.2,1578L1809.2,1578L1809.2,1578z'
              )
              .attr('fill', '#FFFFFF');

            bnbGroup
              .append('path')
              .attr(
                'd',
                'M1692.1,986.4l0.9,193.2l-281.2,165v330.8l-163.3,95.7l-163.3-95.7v-330.8l-281.2-165V986.4L968,889.8l279.5,165.8l281.2-165.8l164.1,96.6H1692.1L1692.1,986.4z M803.9,656.5l443.7-261.6l444.5,261.6l-163.3,96.6l-281.2-165.8L967.2,753.1L803.9,656.5L803.9,656.5z'
              )
              .attr('fill', '#FFFFFF');
            break;
          }
          case ChainType.ARB: {
            const g = d3.select(this);

            // 添加背景圆形
            g.append('circle').attr('r', 27.5).attr('fill', '#2C374B');

            // 创建一个新的 g 元素用于 ARB 图标
            const arbGroup = g
              .append('g')
              .attr('transform', `translate(${-21}, ${-21}) scale(${42 / 56})`); // 缩放以适应节点大小

            // 添加 ARB logo paths
            arbGroup
              .append('path')
              .attr(
                'd',
                'M10.7969 19.768V36.232C10.7969 37.2904 11.3513 38.248 12.2753 38.7856L26.5385 47.0176C27.4457 47.5384 28.5713 47.5384 29.4785 47.0176L43.7417 38.7856C44.6489 38.2648 45.2201 37.2904 45.2201 36.232V19.768C45.2201 18.7096 44.6657 17.752 43.7417 17.2144L29.4785 8.9824C28.5713 8.4616 27.4457 8.4616 26.5385 8.9824L12.2753 17.2144C11.3681 17.7352 10.8137 18.7096 10.8137 19.768H10.7969Z'
              )
              .attr('fill', '#213147');

            arbGroup
              .append('path')
              .attr(
                'd',
                'M31.1077 31.1921L29.0749 36.7697C29.0245 36.9209 29.0245 37.0889 29.0749 37.2569L32.5693 46.8497L36.6181 44.5145L31.7629 31.1921C31.6453 30.8897 31.2253 30.8897 31.1077 31.1921Z'
              )
              .attr('fill', '#12AAFF');

            arbGroup
              .append('path')
              .attr(
                'd',
                'M35.1897 21.8176C35.0721 21.5152 34.6521 21.5152 34.5345 21.8176L32.5017 27.3952C32.4513 27.5464 32.4513 27.7144 32.5017 27.8824L38.2305 43.5904L42.2793 41.2552L35.1897 21.8344V21.8176Z'
              )
              .attr('fill', '#12AAFF');

            arbGroup
              .append('path')
              .attr(
                'd',
                'M28.0001 9.604C28.1009 9.604 28.2017 9.6376 28.2857 9.688L43.7081 18.592C43.8929 18.6928 43.9937 18.8944 43.9937 19.096V36.904C43.9937 37.1056 43.8761 37.3072 43.7081 37.408L28.2857 46.312C28.2017 46.3624 28.1009 46.396 28.0001 46.396C27.8993 46.396 27.7985 46.3624 27.7145 46.312L12.2921 37.408C12.1073 37.3072 12.0065 37.1056 12.0065 36.904V19.0792C12.0065 18.8776 12.1241 18.676 12.2921 18.5752L27.7145 9.6712C27.7985 9.6208 27.8993 9.5872 28.0001 9.5872V9.604ZM28.0001 7C27.4457 7 26.9081 7.1344 26.4041 7.42L10.9817 16.324C9.99054 16.8952 9.38574 17.9368 9.38574 19.0792V36.8872C9.38574 38.0296 9.99054 39.0712 10.9817 39.6424L26.4041 48.5464C26.8913 48.832 27.4457 48.9664 28.0001 48.9664C28.5545 48.9664 29.0921 48.832 29.5961 48.5464L45.0185 39.6424C46.0097 39.0712 46.6145 38.0296 46.6145 36.8872V19.0792C46.6145 17.9368 46.0097 16.8952 45.0185 16.324L29.5793 7.42C29.0921 7.1344 28.5377 7 27.9833 7H28.0001Z'
              )
              .attr('fill', '#9DCCED');

            arbGroup
              .append('path')
              .attr(
                'd',
                'M17.7861 43.6071L19.2141 39.7095L22.0701 42.0783L19.3989 44.5311L17.7861 43.6071Z'
              )
              .attr('fill', '#213147');

            arbGroup
              .append('path')
              .attr(
                'd',
                'M26.6901 17.819H22.7757C22.4901 17.819 22.2213 18.0038 22.1205 18.2726L13.7373 41.255L17.7861 43.5902L27.0261 18.2726C27.1101 18.0374 26.9421 17.8022 26.7069 17.8022L26.6901 17.819Z'
              )
              .attr('fill', 'white');

            arbGroup
              .append('path')
              .attr(
                'd',
                'M33.544 17.819H29.6296C29.344 17.819 29.0752 18.0038 28.9744 18.2726L19.3984 44.5142L23.4472 46.8494L33.8632 18.2726C33.9472 18.0374 33.7792 17.8022 33.544 17.8022V17.819Z'
              )
              .attr('fill', 'white');
            break;
          }
          default: {
            // fallback 为普通圆形
            g.append('circle')
              .attr('r', 27.5)
              .attr('fill', chainColors[d.id as ChainType] || '#1F2937')
              .attr('stroke', '#4B5563')
              .attr('stroke-width', 2);
          }
        }
      } else if (d.level === 1) {
        // 中心节点
        const g = d3.select(this);

        // 添加背景圆形
        g.append('circle')
          .attr('r', 45)
          .attr('fill', 'rgba(255, 4, 4, 0.30)')
          .attr('stroke', '#FF0404')
          .attr('stroke-width', 1);

        // 添加emoji和文本，分三行显示
        g.append('text')
          .attr('text-anchor', 'middle')
          .attr('dy', '-15')
          .attr('fill', '#FFF')
          .attr('font-size', '14px')
          .attr('font-weight', '600')
          .attr('font-family', 'IBM Plex Sans')
          .attr('font-style', 'normal')
          .attr('line-height', 'normal')
          .text('☠️');

        g.append('text')
          .attr('text-anchor', 'middle')
          .attr('dy', '5')
          .attr('fill', '#FFF')
          .attr('font-size', '14px')
          .attr('font-weight', '600')
          .attr('font-family', 'IBM Plex Sans')
          .attr('font-style', 'normal')
          .attr('line-height', 'normal')
          .text('Lazarus');

        g.append('text')
          .attr('text-anchor', 'middle')
          .attr('dy', '25')
          .attr('fill', '#FFF')
          .attr('font-size', '14px')
          .attr('font-weight', '600')
          .attr('font-family', 'IBM Plex Sans')
          .attr('font-style', 'normal')
          .attr('line-height', 'normal')
          .text('Wallets');
      } else {
        // Level 3 nodes
        const g = d3.select(this);
        const nodeColor = chainColors[d.group as ChainType] || '#CCCCCC';
        const gradientId = `node-gradient-${d.id}`;

        // 添加一个透明的可点击区域
        g.append('circle')
          .attr('r', d.size * 1.25)
          .attr('fill', 'transparent')
          .style('cursor', 'pointer');

        // 为 Others 节点添加特殊样式
        if (d.isOthers) {
          // 添加一个带有实线边框的圆形
          const bgCircle = g
            .append('circle')
            .attr('r', d.size)
            .attr('fill', `url(#${gradientId})`)
            .attr('stroke', nodeColor)
            .attr('stroke-width', 0.5)
            .attr('stroke-opacity', 0.8)
            .style('transition', 'all 0.3s ease')
            .style('filter', 'drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.3))');

          // 添加鼠标悬停效果 - 只改变样式，不处理tooltip
          g.on('mouseenter.style', () => {
            bgCircle
              .attr('stroke-width', 1)
              .attr('stroke-opacity', 1)
              .attr('fill-opacity', 1.3)
              .style('filter', 'drop-shadow(0px 0px 4px rgba(0, 0, 0, 0.5))');
          }).on('mouseleave.style', () => {
            bgCircle
              .attr('stroke-width', 0.5)
              .attr('stroke-opacity', 0.8)
              .attr('fill-opacity', 1)
              .style('filter', 'drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.3))');
          });

          // 添加 Others 文本
          g.append('text')
            .text('Others')
            .attr('text-anchor', 'middle')
            .attr('dy', '0.3em')
            .attr('fill', 'white')
            .attr('font-size', `${d.size / 3}px`)
            .attr('font-weight', 'bold');
        } else {
          // 普通第三级节点的样式
          const bgCircle = g
            .append('circle')
            .attr('r', d.size)
            .attr('fill', `url(#${gradientId})`)
            .attr('stroke', nodeColor)
            .attr('stroke-width', 0.5)
            .attr('stroke-opacity', 0.8)
            .style('transition', 'all 0.3s ease')
            .style('filter', 'drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.3))');

          // 添加鼠标悬停效果 - 只改变样式，不处理tooltip
          g.on('mouseenter.style', () => {
            bgCircle
              .attr('stroke-width', 1)
              .attr('stroke-opacity', 1)
              .attr('fill-opacity', 1.3)
              .style('filter', 'drop-shadow(0px 0px 4px rgba(0, 0, 0, 0.5))');
          }).on('mouseleave.style', () => {
            bgCircle
              .attr('stroke-width', 0.5)
              .attr('stroke-opacity', 0.8)
              .attr('fill-opacity', 1)
              .style('filter', 'drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.3))');
          });
        }

        // Add value text
        g.append('text')
          .text((d: Node) => {
            if (d.isOthers) {
              return ''; // Others 节点不显示金额
            }
            return `$${formatNumber(d.value)}`;
          })
          .attr('text-anchor', 'middle')
          .attr('dy', '0.3em')
          .attr('fill', 'white')
          .attr('font-size', `${d.size / 3}px`)
          .attr('font-weight', 'bold');
      }
    });

    // Add labels
    node
      .append('text')
      .text((d) => {
        if (d.level === 3) {
          if (d.isOthers) {
            return ''; // Others 节点不显示底部标签
          }
          return `${d.label.slice(0, 4)}..${d.label.slice(-2)}`;
        }
        return d.level === 1 ? '' : d.label;
      })
      .attr('text-anchor', 'middle')
      .attr('dy', (d) => (d.isSquare ? d.size + 15 : d.size + 15))
      .attr('fill', 'white')
      .attr('font-size', (d) => (d.level === 3 ? '8px' : '12px'))
      .attr('font-family', 'monospace')
      .append('title')
      .text((d) => d.label);

    // Add drag behavior
    const drag = d3
      .drag<SVGGElement, Node>()
      .on('start', (event, d) => {
        if (!event.active) simulation.alphaTarget(0.3).restart();
        d.fx = d.x;
        d.fy = d.y;
        // 安全地设置鼠标样式
        try {
          node.classed('grabbing', true).classed('grab', false);
        } catch (e) {
          // 忽略样式设置错误
          console.warn('Failed to set cursor class on drag start', e);
        }
      })
      .on('drag', (event, d) => {
        d.fx = event.x;
        d.fy = event.y;
      })
      .on('end', (event, d) => {
        if (!event.active) simulation.alphaTarget(0);
        if (d.level === 3) {
          d.fx = null;
          d.fy = null;
        }
        // 安全地设置鼠标样式
        try {
          node.classed('grabbing', false).classed('grab', true);
        } catch (e) {
          // 忽略样式设置错误
          console.warn('Failed to set cursor class on drag end', e);
        }
      });

    node.call(drag as any);

    // 修改 simulation.on('tick') 函数
    simulation.on('tick', () => {
      // 先更新连接线的位置
      link
        .attr('x1', (d: any) => {
          const pos = calculateLinePosition(d.source, d.target);
          return pos.x1;
        })
        .attr('y1', (d: any) => {
          const pos = calculateLinePosition(d.source, d.target);
          return pos.y1;
        })
        .attr('x2', (d: any) => {
          const pos = calculateLinePosition(d.source, d.target);
          return pos.x2;
        })
        .attr('y2', (d: any) => {
          const pos = calculateLinePosition(d.source, d.target);
          return pos.y2;
        });

      linkLabels
        .attr('x', (d: any) => {
          const pos = calculateLinePosition(d.source, d.target);
          // 计算位置更靠近中心节点，取连线起点和终点之间10%的位置
          return pos.x1 + (pos.x2 - pos.x1) * 0.1;
        })
        .attr('y', (d: any) => {
          const pos = calculateLinePosition(d.source, d.target);
          // 计算位置更靠近中心节点，取连线起点和终点之间10%的位置
          return pos.y1 + (pos.y2 - pos.y1) * 0.1;
        })
        .attr('dy', '-0.5em'); // 添加一个小的垂直偏移，使文字更好地对齐

      // 更新节点位置
      node.attr('transform', (d: any) => `translate(${d.x},${d.y})`);
    });

    // 在模拟结束后自动缩放视图以适应所有节点
    simulation.on('end', () => {
      // 不再执行自动缩放
    });

    // 只在初始化时执行一次缩放
    const initialFitView = () => {
      // 计算所有节点的边界框
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;

      nodes.forEach((d: any) => {
        // 考虑节点的大小和位置
        const nodeSize = d.size || 30;
        if (d.x !== undefined && d.y !== undefined) {
          minX = Math.min(minX, d.x - nodeSize);
          minY = Math.min(minY, d.y - nodeSize);
          maxX = Math.max(maxX, d.x + nodeSize);
          maxY = Math.max(maxY, d.y + nodeSize);
        }
      });

      // 添加边距
      const padding = 100; // 增加边距以确保有足够的空间
      minX -= padding;
      minY -= padding;
      maxX += padding;
      maxY += padding;

      // 计算边界框的中心点
      const centerX = (minX + maxX) / 2;
      const centerY = (minY + maxY) / 2;

      // 计算边界框的宽度和高度
      const boxWidth = maxX - minX;
      const boxHeight = maxY - minY;

      // 计算缩放比例，考虑宽高比
      const scale = Math.min(
        width / boxWidth,
        height / boxHeight,
        0.9 // 稍微减小最大缩放以留出更多空间
      );

      // 计算平移量，使边界框中心与容器中心对齐
      const translateX = width / 2 - centerX * scale;
      const translateY = height / 2 - centerY * scale;

      // 应用缩放和平移
      svg
        .transition()
        .duration(750)
        .call(
          zoom.transform,
          d3.zoomIdentity.translate(translateX, translateY).scale(scale)
        );
    };

    // 只在初始化时执行一次缩放
    initialFitView();

    // 创建悬浮提示框 - 放在最后确保它在最上层
    const tooltip = g
      .append('g')
      .attr('class', 'tooltip')
      .style('display', 'none')
      .style('cursor', 'default')
      .style('pointer-events', 'all'); // 确保tooltip可以接收鼠标事件

    // 定义变量和函数
    let isOverNode = false;
    let isOverTooltip = false;
    let hideTimeout: number | null = null;

    // 创建一个新的行容器函数，模拟 flex 布局中的一行
    const createRowContainer = (
      parent: d3.Selection<SVGGElement, unknown, null, undefined>,
      index: number
    ) => {
      // 使用 gap 为 8px 的间距，并考虑顶部 padding
      return parent
        .append('g')
        .attr('transform', `translate(0, ${index * 20 + 12})`); // 20px = 行高 12px + gap 8px，额外加上 12px 的顶部 padding
    };

    // 创建键值对文本，模拟 flex 布局中的一行
    const createKeyValueText = (
      container: d3.Selection<SVGGElement, unknown, null, undefined>,
      key: string,
      valueOptions: {
        text: string;
        x?: number;
        fill?: string;
        fontWeight?: string;
        textDecoration?: string;
        fullText?: string; // 添加完整文本选项
      }
    ) => {
      // 创建键
      createText(container, { text: key });

      // 创建值
      const valueText = createText(container, {
        text: valueOptions.text,
        x: valueOptions.x || 60,
        fill: valueOptions.fill || 'white',
        fontWeight: valueOptions.fontWeight || 'normal',
        textDecoration: valueOptions.textDecoration || 'none',
      });

      // 如果有完整文本，添加复制按钮
      if (valueOptions.fullText) {
        const copyButton = container
          .append('g')
          .attr('transform', `translate(${(valueOptions.x || 60) + 88}, -10)`)
          .style('cursor', 'pointer');

        // 添加一个透明的可点击背景区域
        copyButton
          .append('rect')
          .attr('x', -5)
          .attr('y', -5)
          .attr('width', 25)
          .attr('height', 25)
          .attr('fill', 'transparent')
          .style('cursor', 'pointer');

        // 添加复制图标
        copyButton
          .append('path')
          .attr(
            'd',
            'M1 9.50006C1 10.3285 1.67157 11.0001 2.5 11.0001H4L4 10.0001H2.5C2.22386 10.0001 2 9.7762 2 9.50006L2 2.50006C2 2.22392 2.22386 2.00006 2.5 2.00006L9.5 2.00006C9.77614 2.00006 10 2.22392 10 2.50006V4.00002H5.5C4.67158 4.00002 4 4.67159 4 5.50002V12.5C4 13.3284 4.67158 14 5.5 14H12.5C13.3284 14 14 13.3284 14 12.5V5.50002C14 4.67159 13.3284 4.00002 12.5 4.00002H11V2.50006C11 1.67163 10.3284 1.00006 9.5 1.00006H2.5C1.67157 1.00006 1 1.67163 1 2.50006V9.50006ZM5 5.50002C5 5.22388 5.22386 5.00002 5.5 5.00002H12.5C12.7761 5.00002 13 5.22388 13 5.50002V12.5C13 12.7762 12.7761 13 12.5 13H5.5C5.22386 13 5 12.7762 5 12.5V5.50002Z'
          )
          .attr('fill', '#ffffff')
          .attr('fill-rule', 'evenodd')
          .attr('clip-rule', 'evenodd');

        // 将点击事件添加到整个按钮组
        copyButton.on('click', () => {
          navigator.clipboard
            .writeText(valueOptions.fullText || '')
            .then(() => {
              // 显示复制成功的视觉反馈
              copyButton
                .select('path')
                .attr('fill', '#00ff00')
                .transition()
                .duration(500)
                .attr('fill', '#ffffff');
            });
        });
      }

      return valueText;
    };

    // 在显示 tooltip 时更新内容
    const showTooltip = (
      event: MouseEvent,
      d: Node & { otherAddressCount?: number; othersTotalAmount?: string }
    ) => {
      // 清除任何现有的隐藏计时器
      if (hideTimeout) {
        clearTimeout(hideTimeout);
        hideTimeout = null;
      }

      // 重置 tooltip 内容
      tooltip.selectAll('*').remove();

      // 获取当前节点的位置和尺寸
      const nodeSize = d.size || 27.5;
      const tooltipWidth = 200;
      const tooltipHeight = 80; // 修改 Others 节点的 tooltip 高度为 60

      // 计算tooltip的位置：在节点右侧
      const tooltipX = d.x! + nodeSize + 20;
      const tooltipY = d.y! - tooltipHeight / 2;

      // 设置 tooltip 位置
      tooltip
        .style('display', 'block')
        .style('cursor', 'default')
        .attr('transform', `translate(${tooltipX},${tooltipY})`);

      // 应用统一的 tooltip 样式并获取内容容器
      const content = createTooltipStyle(tooltip, d.level, d.isOthers);

      // 清除之前的内容
      content.selectAll('*').remove();

      switch (d.level) {
        case 3: {
          if (
            d.isOthers &&
            d.otherAddressCount !== undefined &&
            d.othersTotalAmount
          ) {
            // 为 Others 节点创建特定的 tooltip 内容
            // 创建地址数量行
            const addressRow = createRowContainer(content, 0);
            createKeyValueText(addressRow, 'Address:', {
              text: `${d.otherAddressCount} other addresses`,
              x: 65,
              fill: 'white',
              fullText: d.isOthers ? '' : d.label, // 添加完整地址用于复制
            });

            // 创建总金额行
            const amountRow = createRowContainer(content, 1);
            createKeyValueText(amountRow, 'Total Amount:', {
              text: `$${formatNumber(d.othersTotalAmount)}`,
              x: 95, // 增加 x 偏移值，为 Total Amount 文本留出更多空间
              fill: 'white',
            });
          } else {
            // 为第三级节点（地址）创建特定的 tooltip 内容
            // 创建地址行
            const addressRow = createRowContainer(content, 0);
            createKeyValueText(addressRow, 'Address:', {
              text: `${d.label.slice(0, 6)}...${d.label.slice(-4)}`,
              x: 65,
              fill: 'white',
              fullText: d.label, // 添加完整地址用于复制
            });

            // 创建金额行
            const amountRow = createRowContainer(content, 1);
            createKeyValueText(amountRow, 'Amount:', {
              text: `$${d.value}`,
              x: 65,
              fill: 'white',
            });
            // 创建 View on Arkham 行
            const viewRow = createRowContainer(content, 2);
            const viewText = createKeyValueText(viewRow, 'View on:', {
              text: 'Arkham',
              x: 65,
              fill: 'white',
              textDecoration: 'underline',
            });

            // 添加分享图标
            const shareButton = viewRow
              .append('g')
              .attr('transform', `translate(${65 + 45}, -10)`)
              .style('cursor', 'pointer');

            // 添加一个透明的可点击背景区域
            shareButton
              .append('rect')
              .attr('x', -5)
              .attr('y', -5)
              .attr('width', 25)
              .attr('height', 25)
              .attr('fill', 'transparent')
              .style('cursor', 'pointer');

            // 添加分享图标
            shareButton
              .append('path')
              .attr(
                'd',
                'M3 2C2.44772 2 2 2.44772 2 3V12C2 12.5523 2.44772 13 3 13H12C12.5523 13 13 12.5523 13 12V8.5C13 8.22386 12.7761 8 12.5 8C12.2239 8 12 8.22386 12 8.5V12H3V3L6.5 3C6.77614 3 7 2.77614 7 2.5C7 2.22386 6.77614 2 6.5 2H3ZM12.8536 2.14645C12.9015 2.19439 12.9377 2.24964 12.9621 2.30861C12.9861 2.36669 12.9996 2.4303 13 2.497L13 2.5V2.50049V5.5C13 5.77614 12.7761 6 12.5 6C12.2239 6 12 5.77614 12 5.5V3.70711L6.85355 8.85355C6.65829 9.04882 6.34171 9.04882 6.14645 8.85355C5.95118 8.65829 5.95118 8.34171 6.14645 8.14645L11.2929 3H9.5C9.22386 3 9 2.77614 9 2.5C9 2.22386 9.22386 2 9.5 2H12.4999H12.5C12.5678 2 12.6324 2.01349 12.6914 2.03794C12.7504 2.06234 12.8056 2.09851 12.8536 2.14645Z'
              )
              .attr('fill', '#ffffff')
              .attr('fill-rule', 'evenodd')
              .attr('clip-rule', 'evenodd');

            // 添加点击事件
            const handleViewClick = () => {
              window.open(
                `https://intel.arkm.com/explorer/address/${d.label}`,
                '_blank'
              );
            };
            viewText.style('cursor', 'pointer').on('click', handleViewClick);
            shareButton.on('click', handleViewClick);
          }
          break;
        }

        case 2: {
          // 为第二级节点（链）创建特定的 tooltip 内容
          const chainData = hackerAddressTopList.chainAssetList.find(
            (chain) => chain.chainName === d.label
          );

          if (chainData) {
            // 创建 Network 行
            const networkRow = createRowContainer(content, 0);
            createKeyValueText(networkRow, 'Network:', {
              text: chainNames[chainData.chainName as ChainType],
              x: 65,
              fill: 'white',
            });

            // 创建 Amount 行
            const amountRow = createRowContainer(content, 1);
            createKeyValueText(amountRow, 'Amount:', {
              text: `$${formatNumber(chainData.totalAmount)}`,
              x: 65,
              fill: 'white',
            });

            // 创建百分比行
            const percentageRow = createRowContainer(content, 2);
            createKeyValueText(percentageRow, 'Percentage:', {
              text:
                chainData.amountRatio === '0'
                  ? '<0.01%'
                  : `${chainData.amountRatio}%`,
              x: 75,
              fill: 'white',
            });

            // 创建钱包数量行
            const walletsRow = createRowContainer(content, 3);
            createKeyValueText(walletsRow, 'Wallets:', {
              text: chainData.addressCount.toString(),
              x: 55,
              fill: 'white',
            });
          }
          break;
        }
      }
    };

    const hideTooltip = () => {
      if (!isOverNode && !isOverTooltip) {
        tooltip.style('display', 'none');
      }
    };

    // 修改鼠标事件处理
    node.each(function (d) {
      if (d.level === 3) {
        // 添加鼠标样式
        d3.select(this).style('cursor', 'pointer');

        // 节点的鼠标事件
        d3.select(this)
          .on('mouseover.tooltip', (event: MouseEvent) => {
            isOverNode = true;
            // 如果是 Others 节点，显示特殊的 tooltip
            if (d.isOthers) {
              const chainData = hackerAddressTopList.chainAssetList.find(
                (chain) => chain.chainName === d.group
              );
              if (chainData) {
                showTooltip(event, {
                  ...d,
                  otherAddressCount:
                    chainData.addressCount - chainData.topAddressList.length,
                  othersTotalAmount: chainData.othersTotalAmount,
                });
              }
              return;
            }
            showTooltip(event, d);
          })
          .on('mousemove.tooltip', (event: MouseEvent) => {
            if (isOverNode) {
              // 如果是 Others 节点，显示特殊的 tooltip
              if (d.isOthers) {
                const chainData = hackerAddressTopList.chainAssetList.find(
                  (chain) => chain.chainName === d.group
                );
                if (chainData) {
                  showTooltip(event, {
                    ...d,
                    otherAddressCount:
                      chainData.addressCount - chainData.topAddressList.length,
                    othersTotalAmount: chainData.othersTotalAmount,
                  });
                }
                return;
              }
              showTooltip(event, d);
            }
          })
          .on('mouseout.tooltip', () => {
            isOverNode = false;
            hideTimeout = window.setTimeout(hideTooltip, 500);
          });
      }
    });

    // tooltip 的鼠标事件移到最外层的 tooltip 组
    tooltip
      .on('mouseover', () => {
        isOverTooltip = true;
        // 确保鼠标样式为默认
        tooltip.style('cursor', 'default');
        if (hideTimeout) {
          window.clearTimeout(hideTimeout);
          hideTimeout = null;
        }
      })
      .on('mouseout', () => {
        isOverTooltip = false;
        hideTimeout = window.setTimeout(hideTooltip, 500);
      });

    // 为第二级节点添加鼠标悬停事件
    node
      .filter((d) => d.level === 2)
      .style('cursor', 'pointer')
      .each(function (d) {
        // 为每个第二级节点添加一个透明的背景圆，使整个区域都能触发悬停事件
        // 这个背景圆比实际节点大，确保整个区域都能触发事件
        const nodeSize = d.size || 27.5;
        d3.select(this)
          .insert('circle', ':first-child') // 在最底层插入背景圆
          .attr('r', nodeSize * 1.5) // 背景圆比实际节点大50%
          .attr('fill', 'transparent') // 透明填充
          .attr('stroke', 'none') // 无边框
          .attr('pointer-events', 'all'); // 确保能捕获鼠标事件
      })
      .on('mouseenter.tooltip', function (event, d) {
        // 显示工具提示
        isOverNode = true;
        showTooltip(event, d);

        // 降低所有连接线的不透明度
        link.attr('stroke-opacity', 0.1);

        // 高亮显示从中心到当前链节点的连接线
        link
          .filter((l: any) => {
            const sourceId =
              l.source instanceof Object ? l.source.id : l.source;
            const targetId =
              l.target instanceof Object ? l.target.id : l.target;
            return sourceId === 'center' && targetId === d.id;
          })
          .attr('stroke-opacity', 1)
          .attr('stroke-width', lineWidth * 2.5)
          .attr(
            'stroke',
            (l: any) => l.data?.selectColor || l.color || '#FFFFFF'
          );

        // 降低所有标签的不透明度
        linkLabels.attr('opacity', 0.2);

        // 高亮显示当前链节点的标签
        linkLabels
          .filter((l: any) => {
            const targetId =
              l.target instanceof Object ? l.target.id : l.target;
            return targetId === d.id;
          })
          .attr('font-size', '14px')
          .attr('font-weight', 'bolder')
          .attr('fill', (l: any) => l.data?.selectColor || l.color || '#FFFFFF')
          .attr('opacity', 1);

        // 淡化所有节点
        node.style('opacity', 0.3);

        // 只高亮显示中心节点和当前链节点，不高亮子节点
        node
          .filter((n: any) => n.id === 'center' || n.id === d.id)
          .style('opacity', 1);

        // 为高亮的节点添加发光效果
        d3.select(this)
          .select('circle')
          .style('filter', 'drop-shadow(0px 0px 8px rgba(255, 255, 255, 0.8))');
      })
      .on('mousemove.tooltip', function (event, d) {
        // 更新工具提示位置
        if (isOverNode) {
          showTooltip(event, d);
        }
      })
      .on('mouseleave.tooltip', function () {
        // 隐藏工具提示
        isOverNode = false;
        hideTimeout = window.setTimeout(hideTooltip, 500);

        // 恢复所有连接线的不透明度和宽度
        link
          .attr('stroke-opacity', 0.5)
          .attr('stroke-width', lineWidth)
          .attr('stroke', (d) => d.color || '#4B5563');

        // 恢复标签大小和粗细
        linkLabels
          .attr('font-size', '12px')
          .attr('font-weight', 'bold')
          .attr('fill', (d) => d.color || 'white')
          .attr('opacity', 1);

        // 恢复所有节点的不透明度
        node.style('opacity', 1);

        // 移除发光效果
        node.select('circle:not(:first-child)').style('filter', (n: any) => {
          if (n.level === 3) {
            return 'drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.3))';
          }
          return null;
        });
      });

    // 在组件卸载时清理
    return cleanup;
  }, [dimensions, hackerAddressTopList]); // 切换 tab 时触发重新渲染

  // 添加 fitView 函数（示例，您可以根据需要实现）
  const fitView = () => {
    if (!svgRef.current) return;

    // 获取 SVG 元素和 d3 缩放对象
    const svg = d3.select(svgRef.current);
    const g = svg.select('g');

    // 获取所有节点
    const nodes = g.selectAll('.nodes g').data();

    // 计算所有节点的边界框
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    nodes.forEach((d: any) => {
      // 考虑节点的大小
      const nodeSize = d.size || 30;
      minX = Math.min(minX, d.x - nodeSize);
      minY = Math.min(minY, d.y - nodeSize);
      maxX = Math.max(maxX, d.x + nodeSize);
      maxY = Math.max(maxY, d.y + nodeSize);
    });

    // 添加一些边距
    const padding = 50;
    minX -= padding;
    minY -= padding;
    maxX += padding;
    maxY += padding;

    // 计算边界框的宽度和高度
    const boxWidth = maxX - minX;
    const boxHeight = maxY - minY;

    // 计算缩放比例
    const scale = Math.min(
      dimensions.width / boxWidth,
      dimensions.height / boxHeight,
      1 // 限制最大缩放为1，避免过度放大
    );

    // 计算平移量，使边界框居中
    const translateX = (dimensions.width - boxWidth * scale) / 2 - minX * scale;
    const translateY =
      (dimensions.height - boxHeight * scale) / 2 - minY * scale;

    // 应用缩放和平移
    const zoom = d3.zoom().on('zoom', (event) => {
      g.attr('transform', event.transform);
    });

    svg
      .transition()
      .duration(750)
      .call(
        zoom.transform,
        d3.zoomIdentity.translate(translateX, translateY).scale(scale)
      );
  };

  // 添加缩放功能
  const zoomIn = () => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    const g = svg.select('g');

    // 获取当前变换
    const transform = d3.zoomTransform(svg.node() as Element);

    // 创建缩放行为
    const zoom = d3.zoom().on('zoom', (event) => {
      g.attr('transform', event.transform);
    });

    // 放大 1.2 倍
    svg.transition().duration(300).call(zoom.transform, transform.scale(1.2));
  };

  const zoomOut = () => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    const g = svg.select('g');

    // 获取当前变换
    const transform = d3.zoomTransform(svg.node() as Element);

    // 创建缩放行为
    const zoom = d3.zoom().on('zoom', (event) => {
      g.attr('transform', event.transform);
    });

    // 缩小到 0.8 倍
    svg.transition().duration(300).call(zoom.transform, transform.scale(0.8));
  };

  // 添加全屏切换功能
  const toggleFullScreen = () => {
    if (!containerRef.current) return;

    if (!isFullScreen) {
      // 进入全屏
      if (containerRef.current.requestFullscreen) {
        containerRef.current
          .requestFullscreen()
          .then(() => {
            setIsFullScreen(true);
          })
          .catch((err) => console.error('全屏模式出错:', err));
      }
    } else {
      // 退出全屏
      if (document.exitFullscreen) {
        document
          .exitFullscreen()
          .then(() => {
            setIsFullScreen(false);
          })
          .catch((err) => console.error('退出全屏模式出错:', err));
      }
    }
  };

  // 监听全屏状态变化
  useEffect(() => {
    const handleFullScreenChange = () => {
      const isFullscreenNow = !!document.fullscreenElement;
      setIsFullScreen(isFullscreenNow);
      // 仅在全屏状态变化时更新尺寸
      if (!containerRef.current) {
        setDimensions({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight || 720,
        });
      }
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="w-full h-full relative" // 添加 relative 以便绝对定位
      style={{
        backgroundColor: isHovered ? '#1E2023' : '#101014',
        transition: 'background-color 0.3s ease',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* 控制按钮区域 */}
      <div className="absolute bottom-4 left-4 flex items-center">
        <ReactFlowProvider>
          <Controls
            orientation="horizontal"
            showInteractive={false}
            showFitView={false}
            onZoomIn={zoomIn}
            onZoomOut={zoomOut}
          >
            <ControlButton onClick={toggleFullScreen}>
              {isFullScreen ? <ExitFullScreenIcon /> : <EnterFullScreenIcon />}
            </ControlButton>
          </Controls>
        </ReactFlowProvider>
      </div>

      {loading ? (
        // <div className="w-full h-full flex items-center justify-center">
        //   <Spin />
        // </div>
        <CommonLoading loading={loading} backgroundColor="rgba(16,16,20,1)" />
      ) : (
        <svg
          ref={svgRef}
          width={dimensions.width}
          height={dimensions.height}
          className="w-full h-full"
          style={{ cursor: 'grab' }}
        >
          <defs>
            <style type="text/css">
{`
                .grabbing { cursor: grabbing !important; }
                .grab { cursor: grab !important; }
              
`}</style>
          </defs>
        </svg>
      )}
    </div>
  );
};

export default Bubble;
